import React, { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { authService } from "../../api/services/authService";
import {
  EditProfileFormData,
  ChangePasswordFormData,
} from "../../schemas/profileSchema";
import EditProfileForm from "../../components/forms/EditProfileForm";
import ChangePasswordForm from "../../components/forms/ChangePasswordForm";
import Modal from "../../components/common/Modal";
import { PencilIcon, KeyIcon } from "@heroicons/react/24/outline";
import Loader from "../../components/common/Loader";

const Profile: React.FC = () => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  const editProfileMutation = useMutation({
    mutationFn: authService.editProfile,
    onSuccess: (updatedUser) => {
      // Update localStorage with new profile
      localStorage.setItem("profile", JSON.stringify(updatedUser));
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setIsEditProfileOpen(false);
      toast.success("Profile updated successfully");
      // Reload page to update profile in context
      window.location.reload();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update profile");
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: authService.changePassword,
    onSuccess: () => {
      setIsChangePasswordOpen(false);
      toast.success("Password changed successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to change password");
    },
  });

  const handleEditProfile = (data: EditProfileFormData) => {
    editProfileMutation.mutate(data);
  };

  const handleChangePassword = (data: ChangePasswordFormData) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen px-6 py-4">
      <div className="max-w-4xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Profile</h1>

        {/* Profile Information Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-semibold text-gray-800">
              Personal Information
            </h2>
            <button
              onClick={() => setIsEditProfileOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-default text-white rounded hover:bg-primary-light transition-colors"
            >
              <PencilIcon className="h-4 w-4" />
              <span>Edit Profile</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">Name</label>
              <p className="mt-1 text-base text-gray-900">{profile.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Username
              </label>
              <p className="mt-1 text-base text-gray-900">
                {profile.username || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-base text-gray-900">{profile.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Phone Number
              </label>
              <p className="mt-1 text-base text-gray-900">
                {profile.phoneNumber || "Not provided"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Role</label>
              <p className="mt-1 text-base text-gray-900">
                {profile.role || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Password
              </h2>
              <p className="text-sm text-gray-500">
                Update your password to keep your account secure
              </p>
            </div>
            <button
              onClick={() => setIsChangePasswordOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-default text-white rounded hover:bg-primary-light transition-colors"
            >
              <KeyIcon className="h-4 w-4" />
              <span>Change Password</span>
            </button>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {isEditProfileOpen && (
        <Modal
          isOpen={isEditProfileOpen}
          onClose={() => setIsEditProfileOpen(false)}
          title="Edit Profile"
          size="md"
        >
          <EditProfileForm
            initialData={profile}
            onSubmit={handleEditProfile}
            onCancel={() => setIsEditProfileOpen(false)}
            isLoading={editProfileMutation.isPending}
          />
        </Modal>
      )}

      {/* Change Password Modal */}
      {isChangePasswordOpen && (
        <Modal
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
          title="Change Password"
          size="md"
        >
          <ChangePasswordForm
            onSubmit={handleChangePassword}
            onCancel={() => setIsChangePasswordOpen(false)}
            isLoading={changePasswordMutation.isPending}
          />
        </Modal>
      )}
    </div>
  );
};

export default Profile;
